import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CommonService, TrackingModelService } from '@frontend/promo-homewidget';
import { UserService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';

import { WebAnalyticModel } from '../../../../common/web-tracking/web-analytic.model';
import { CommonEventService } from '../events/common-event.service';
import { TargetPromotionMetadata } from '../models/target-promotion-metadata';
import {
    targetPromotionInit,
    userOptedFailedForExpiredPromo,
    userOptedFailedForPromotion,
    userOptedInForPromotion,
} from '../store/target-promotions.actions';

@Component({
    selector: 'app-target-promotion-base',
    template: '',
    standalone: true,
})
export class TargetPromotionBaseComponent implements OnInit, OnDestroy {
    public readonly leaderboard: string = 'leaderboard';
    public readonly ranking: string = 'ranking';
    alive: Subject<boolean> = new Subject<boolean>();
    targetPromotionMetaData: TargetPromotionMetadata;
    isAutoOptin: boolean;
    promoId: string;
    currentTab: string;
    webAnalyticModel = new WebAnalyticModel();

    constructor(
        public router: Router,
        public store: Store,
        public trackingModelService: TrackingModelService,
        public route: ActivatedRoute,
        public userService: UserService,
        public commonService: CommonService,
        public commonEventService: CommonEventService,
    ) {}

    ngOnInit() {
        if (!this.userService.isAuthenticated) {
            this.commonService.goToOverviewPage();
            return;
        }
        this.store.dispatch(targetPromotionInit());
        this.promoId = this.route.snapshot.paramMap.get('promoId') ?? '';
        this.isAutoOptin = this.route.snapshot.queryParamMap.get('autooptin') === 'true';
        this.removeAutoOptin();
    }

    ngOnDestroy() {
        this.alive.next(false);
        this.alive.complete();
    }

    autoOptin(promoId: string) {
        if (this.isAutoOptin && this.targetPromotionMetaData) {
            const promoEndTime = new Date(this.targetPromotionMetaData.promoEndTime);
            if (promoEndTime < new Date()) {
                this.trackMessage(promoId, true);
                this.store.dispatch(userOptedFailedForExpiredPromo());
                this.isAutoOptin = false;
            } else {
                if (this.targetPromotionMetaData.optinDetails?.isOptedIn == false) {
                    this.store.dispatch(userOptedInForPromotion({ promoId }));
                    this.isAutoOptin = false;
                } else if (this.targetPromotionMetaData.optinDetails?.isOptedIn) {
                    this.trackMessage(promoId, false);
                    this.store.dispatch(userOptedFailedForPromotion());
                    this.isAutoOptin = false;
                }
            }
        }
    }

    removeAutoOptin() {
        if (this.isAutoOptin) {
            this.router.navigate([], {
                queryParams: {
                    autooptin: null,
                },
                queryParamsHandling: 'merge',
            });
        }
    }

    trackMessage(promoId: string, isPromoExpired: boolean) {
        this.trackingModelService.submitTracking({
            CategoryEvent: 'promo hub',
            LabelEvent: 'opt in',
            ActionEvent: isPromoExpired ? 'error' : 'success',
            PositionEvent: promoId,
            EventDetails: isPromoExpired ? 'promotion either expired/invalid' : 'opted in / already opted in',
            LocationEvent: 'offer page',
        });
    }

    goToValidPromoTab(tabsAvailable: string[]) {
        if (!this.promoId || tabsAvailable?.indexOf(this.promoId) >= 0) {
            this.commonService.goToOverviewPage();
        }
        if (tabsAvailable?.length > 0) {
            let stopProcess = true;
            const urlSegments = window.location.pathname.split(this.promoId);
            const url = urlSegments.length > 1 ? urlSegments[urlSegments.length - 1] : '';
            for (let i = 0; i < tabsAvailable.length; i++) {
                if (url.indexOf(tabsAvailable[i]) > 0) {
                    stopProcess = false;
                    this.currentTab = tabsAvailable[i];
                }
            }
            if (stopProcess) {
                const tab = tabsAvailable[0] == this.leaderboard ? this.ranking : tabsAvailable[0];
                this.router.navigate([`./${tab}`], {
                    relativeTo: this.route,
                    replaceUrl: true,
                    queryParamsHandling: 'preserve',
                });
                this.currentTab = tabsAvailable[0];
            }
            this.webAnalyticModel.tab = this.currentTab;
        }
        this.webAnalyticModel.promoId = this.promoId;
        this.commonEventService.sendWAModel(this.webAnalyticModel);
    }
}
